import React from "react";
import * as stylex from "@stylexjs/stylex";
import { Avatar } from "@zendeskgarden/react-avatars";
import { useMutation } from "urql";
import { timeAgo } from "~/utils/timeAgoLocaleEn";
import IssueIcon from "~/icons/IssueIcon";
import type { Issue } from "~/types";
import { DeleteAttachmentMutation, type IDeleteAttachmentMutation } from "../queries";
import "../styles/variables.stylex";
import { ZendeskThemeVariables } from "../styles/variables.stylex";
import { Label } from "./Label";
import { FullViewPrompt } from "./FullViewPrompt";
type Props = {
  // Attachment ID
  attachmentId?: string;
  // Linear issue to be shown.
  issue: Issue;
  // Should unlinking be allowed
  allowUnlink?: boolean;
  // Callback to be executed after unlinking the attachment
  onUnlink?(): void;
};

/**
 * Renders the linked-issue summary for a Zendesk ticket, including key metadata and optional unlink controls.
 *
 * Use this after a ticket has been attached to a Linear issue or when previewing a selected issue in the link flow.
 * Pair it with {@link ProjectWidget} when the integration also allows linking Zendesk tickets to projects.
 */
export const IssueWidget = (props: Props) => {
  const {
    attachmentId,
    issue,
    allowUnlink,
    onUnlink
  } = props;
  const createdAt = new Date(issue.createdAt);
  const updatedAt = new Date(issue.updatedAt);
  const [confirmUnlink, setConfirmUnlink] = React.useState(false);
  const [_deleteAttachmentResult, deleteAttachment] = useMutation<IDeleteAttachmentMutation>(DeleteAttachmentMutation);
  const handleUnlink = (event: React.MouseEvent) => {
    event.preventDefault();
    setConfirmUnlink(true);
  };
  const processUnlink = () => {
    if (attachmentId && onUnlink) {
      setConfirmUnlink(false);
      void deleteAttachment({
        id: attachmentId
      }).then(onUnlink);
    }
  };
  return <div className="sx-78zum5 sx-dt5ytf">
      {confirmUnlink && <FullViewPrompt title="Unlink this issue?" description="Linear issue won't be deleted." confirmLabel="Unlink ticket" onConfirm={processUnlink} onCancel={() => setConfirmUnlink(false)} />}
      <div className="sx-78zum5 sx-1qughib sx-od5an3">
        <div className="sx-78zum5 sx-6s0dn4 sx-17d4w8g">
          <IssueIcon />
          <a href={issue.url} target="_blank" className="sx-1s688f">
            {issue.identifier}
          </a>
        </div>
        <div>
          {allowUnlink && <>
              <a onClick={handleUnlink} href="" className="sx-fifm61">
                Unlink
              </a>
              <span className="sx-t970qd sx-1xijbu4 sx-8qomae">&middot;</span>
            </>}

          <a href={issue.url} target="_blank" className="sx-fifm61">
            View in Linear
          </a>
        </div>
      </div>
      <div className="sx-k50ysn sx-od5an3">{issue.title}</div>
      <div className="sx-78zum5 sx-dt5ytf sx-fifm61 sx-od5an3">
        <div className="sx-78zum5 sx-so031l sx-1q0q8m5 sx-1f0u6lw">
          <div className="sx-78zum5 sx-6s0dn4 sx-1t0vds8 sx-5k8d2m sx-b3r6kr sx-nnjuj9">Status</div>
          <div className="sx-78zum5 sx-98rzlu sx-6s0dn4 sx-1t0vds8 sx-5k8d2m sx-b3r6kr">{issue.state.name}</div>
        </div>
        <div className="sx-78zum5 sx-so031l sx-1q0q8m5 sx-1f0u6lw">
          <div className="sx-78zum5 sx-6s0dn4 sx-1t0vds8 sx-5k8d2m sx-b3r6kr sx-nnjuj9">Priority</div>
          <div className="sx-78zum5 sx-98rzlu sx-6s0dn4 sx-1t0vds8 sx-5k8d2m sx-b3r6kr">{issue.priorityLabel}</div>
        </div>
        <div className="sx-78zum5 sx-so031l sx-1q0q8m5 sx-1f0u6lw">
          <div className="sx-78zum5 sx-6s0dn4 sx-1t0vds8 sx-5k8d2m sx-b3r6kr sx-nnjuj9">Assignee</div>
          <div className="sx-78zum5 sx-98rzlu sx-6s0dn4 sx-1t0vds8 sx-5k8d2m sx-b3r6kr">
            {issue.assignee ? <>
                {issue.assignee.avatarUrl && <Avatar className="sx-1q08448" size="extraextrasmall">
                    <img src={issue.assignee.avatarUrl} />
                  </Avatar>}

                {issue.assignee.name}
              </> : <span className="sx-8qomae">Unassigned</span>}
          </div>
        </div>
        <div className="sx-78zum5 sx-so031l sx-1q0q8m5 sx-1f0u6lw">
          <div className="sx-78zum5 sx-6s0dn4 sx-1t0vds8 sx-5k8d2m sx-b3r6kr sx-nnjuj9">Labels</div>
          <div className="sx-78zum5 sx-98rzlu sx-6s0dn4 sx-1t0vds8 sx-5k8d2m sx-b3r6kr">
            <div className="sx-78zum5 sx-1a02dak sx-1jnr06f sx-193iq5w sx-b3r6kr">
              {issue.labels.nodes.length ? issue.labels.nodes.map(label => <Label label={label} key={label.id} />) : <span className="sx-8qomae">No labels</span>}
            </div>
          </div>
        </div>
        {issue.cycle && <div className="sx-78zum5 sx-so031l sx-1q0q8m5 sx-1f0u6lw">
            <div className="sx-78zum5 sx-6s0dn4 sx-1t0vds8 sx-5k8d2m sx-b3r6kr sx-nnjuj9">Cycle</div>
            <div className="sx-78zum5 sx-98rzlu sx-6s0dn4 sx-1t0vds8 sx-5k8d2m sx-b3r6kr">
              {issue.cycle.name ? `${issue.cycle.name} (Cycle ${issue.cycle.number})` : `Cycle ${issue.cycle.number}`}
            </div>
          </div>}
        {issue.project && <div className="sx-78zum5 sx-so031l sx-1q0q8m5 sx-1f0u6lw">
            <div className="sx-78zum5 sx-6s0dn4 sx-1t0vds8 sx-5k8d2m sx-b3r6kr sx-nnjuj9">Project</div>
            <div className="sx-78zum5 sx-98rzlu sx-6s0dn4 sx-1t0vds8 sx-5k8d2m sx-b3r6kr">{issue.project.name}</div>
          </div>}
        <div className="sx-78zum5 sx-so031l sx-1q0q8m5 sx-1f0u6lw">
          <div className="sx-78zum5 sx-6s0dn4 sx-1t0vds8 sx-5k8d2m sx-b3r6kr sx-nnjuj9">Ticket count</div>
          <div className="sx-78zum5 sx-98rzlu sx-6s0dn4 sx-1t0vds8 sx-5k8d2m sx-b3r6kr">{issue.customerTicketCount}</div>
        </div>
      </div>
      <span className="sx-fifm61 sx-8qomae">Created {timeAgo.format(createdAt, "round")}</span>
      {issue.createdAt !== issue.updatedAt && <span className="sx-fifm61 sx-8qomae">Updated {timeAgo.format(updatedAt, "round")}</span>}
    </div>;
};